<template>
  <HotelOffersListModal :isOpen="isHotelOffersModalOpen" @closeModal="hideOffers" />
  <div
    class="tw:flex tw:gap-4 tw:flex-col tw:md:flex-row"
    :class="activeTab !== null ? 'tw:items-stretch' : 'tw:items-center'"
  >
    <div class="tw:w-full tw:md:w-1/2 tw:flex-1">
      <div
        v-for="item in products"
        :key="item.id"
        :class="getActiveClass(item)"
        class="tw:last:mb-0 tw:mb-2 tw:p-4 tw:flex tw:flex-col tw:rounded-xl tw:gap-x-4 tw:w-full"
      >
        <StudioPlusProduct :item="item" :activeTab="activeTab" @openTab="openTab" @showOffers="showOffers" />
      </div>
    </div>
    <div class="tw:hidden tw:md:flex tw:md:w-1/2 tw:flex-1 tw:rounded-xl tw:items-center" :class="getBgClass()">
      <Transition name="fade" mode="out-in">
        <Videoplayer
          :autoplay="true"
          :src="videoUrl"
          height="400px"
          width="100%"
          :controls="false"
          :id="activeTab"
          :key="activeTab"
          @clickEventTriggered="clickEventTriggered"
        />
      </Transition>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import Videoplayer from '@/components/studioPlus/Videoplayer.vue'
import HotelOffersListModal from '@/components/hotelOffers/HotelOffersListModal.vue'
import StudioPlusProduct from '@/components/studioPlus/StudioPlusProduct.vue'

import appAnalytics from '@/utils/tracking'

import { selectedAccommodationId } from '@/layouts/queries'
import { segmentEventsNamesVideo, segmentEventsNamesTile } from '@/constants/studioPlusProductEvents.ts'

const HotelConnect = new URL('/src/assets/img/studio-plus/icons/HotelConnect.svg', import.meta.url)
const RateInsights = new URL('/src/assets/img/studio-plus/icons/RateInsights.svg', import.meta.url)
const TravelerTrends = new URL('/src/assets/img/studio-plus/icons/TravelerTrends.svg', import.meta.url)
const HotelOffers = new URL('/src/assets/img/studio-plus/icons/HotelOffers.svg', import.meta.url)
const HotelConnectVideo = new URL('@/assets/videos/HotelConnect.mp4', import.meta.url)
const RateInsightsVideo = new URL('@/assets/videos/RateInsights.mp4', import.meta.url)
const TravelerTrendsVideo = new URL('@/assets/videos/VisitorsProfile.mp4', import.meta.url)
const HotelOffersVideo = new URL('@/assets/videos/HotelOffers.mp4', import.meta.url)

const isHotelOffersModalOpen = ref(false)
const activeTab = ref(3)

const products = [
  {
    title: 'hotel_offers_product_header',
    id: 3,
    subtitle: 'plus_product_page_ho_subtext',
    icon: HotelOffers,
    isDisabled: false,
    list: ['plus_product_page_ho_point_1', 'plus_product_page_ho_point_2', 'plus_product_page_ho_point_3'],
  },
  {
    title: 'hotel_connect',
    id: 0,
    subtitle: 'plus_product_page_hc_subtext',
    icon: HotelConnect,
    isDisabled: false,
    list: ['plus_product_page_hc_point_1', 'plus_product_page_hc_point_2', 'plus_product_page_hc_point_3'],
  },
  {
    title: 'app.rateInsights.name',
    id: 1,
    subtitle: 'plus_product_page_ri_subtext',
    icon: RateInsights,
    isDisabled: false,
    list: ['plus_product_page_ri_point_1', 'plus_product_page_ri_point_2', 'plus_product_page_ri_point_3'],
  },
  {
    title: 'app.visitorsProfile.name',
    id: 2,
    subtitle: 'plus_product_page_vp_subtext',
    icon: TravelerTrends,
    isDisabled: false,
    list: ['plus_product_page_vp_point_1', 'plus_product_page_vp_point_2', 'plus_product_page_vp_point_3'],
  },
]

const videoUrl = computed(() => {
  if (activeTab.value === 0) return HotelConnectVideo
  if (activeTab.value === 1) return RateInsightsVideo
  if (activeTab.value === 2) return TravelerTrendsVideo
  if (activeTab.value === 3) return HotelOffersVideo
  else return null
})

const clickEventTriggered = id => {
  appAnalytics.track(segmentEventsNamesVideo[id], {
    accommodationId: selectedAccommodationId.value,
    source: 'Home Page',
  })
}
const showOffers = () => {
  appAnalytics.track(appAnalytics.events.SP_SHOW_OFFERS, {
    accommodationId: selectedAccommodationId.value,
    source: 'Home Page',
  })
  isHotelOffersModalOpen.value = true
}
const hideOffers = () => {
  isHotelOffersModalOpen.value = false
}

const openTab = (event, id) => {
  if (event.target?.id === 'show-offers-btn') return

  if (id !== activeTab.value) {
    appAnalytics.track(segmentEventsNamesTile[id], {
      accommodationId: selectedAccommodationId.value,
      source: 'Home Page',
    })
  }
  activeTab.value = id === activeTab.value ? 3 : id
}

const getActiveClass = item => {
  const classes = []
  if (!item.isDisabled) {
    classes.push('tw:hover:cursor-pointer')
  }
  if (activeTab.value === item.id) {
    if (activeTab.value === 0) {
      classes.push('tw:bg-orange-100')
    } else if (activeTab.value === 1) {
      classes.push('tw:bg-red-100')
    } else if (activeTab.value === 2) {
      classes.push('tw:bg-blue-100')
    } else if (activeTab.value === 3) {
      classes.push('tw:bg-green-100')
    }
  }
  return classes.join(' ')
}

const getBgClass = () => {
  if (activeTab.value === 0) {
    return 'tw:bg-orange-100'
  } else if (activeTab.value === 1) {
    return 'tw:bg-red-100'
  } else if (activeTab.value === 2) {
    return 'tw:bg-blue-100'
  } else if (activeTab.value === 3) {
    return 'tw:bg-green-100'
  }
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
